import { api } from './client';

export interface PackingItem {
  id: number;
  itemCode: string | null;
  itemName: string;
  unit: string;
  doQty: string;
  pickedQty: string;
  packedQty: string;
  condition: 'OK' | 'Damaged' | 'Short' | 'Replaced';
  conditionNote: string | null;
  imageUrl?: string | null;
  barcode?: string | null;
}

export interface PackingBox {
  id: number;
  packingId: number;
  boxNo: number;
  itemCode: string;
  qty: string;
  weightKg: string | null;
  lengthCm: string | null;
  widthCm: string | null;
  heightCm: string | null;
}

export interface Packing {
  id: number;
  packNo: string;
  pickListId: number;
  packDate: string;
  supervisor: string | null;
  boxCount: number;
  totalWeightKg: string;
  sealNo: string | null;
  supervisorConfirmed: boolean;
  managerConfirmed: boolean;
  status: 'Packing' | 'Verified' | 'GRN-Out' | 'Rejected';
  rejected: boolean;
  rejectionReason: string | null;
  remarks: string | null;
  items: PackingItem[];
  boxes: PackingBox[];
  pickList?: {
    id: number;
    pickNo: string;
    /** Segregated-items photos taken at picking (JSON array of URLs). */
    photoUrls?: string | null;
    dispatchOrder?: {
      id: number;
      doNo: string;
      remarks?: string | null;
      party?: { name: string };
      enquiry?: { enqNo: string; source?: string | null; remarks?: string | null };
    };
  };
  onlineCustomer?: string | null;
  /** Shopify order number (list rows of an online order). */
  onlineOrderName?: string | null;
  /** Shopify order number + buyer, on a single packing of an online order. */
  onlineOrder?: { orderName: string | null; customerName: string | null } | null;
}

// ── Scan types ──────────────────────────────────────────────────────────────

export interface PackingSerialEntry {
  id: number;
  serialNo: string;
  scannedAt: string;
}

export interface PackingItemScanState {
  id: number;
  itemCode: string | null;
  itemName: string;
  unit: string;
  targetQty: number;
  packedQty: number;
  serials: PackingSerialEntry[];
  isComplete: boolean;
}

export interface PackingScanStatus {
  packingId: number;
  packNo: string;
  status: string;
  doNo?: string;
  customer?: string;
  items: PackingItemScanState[];
  totalTarget: number;
  totalScanned: number;
  allComplete: boolean;
}

// ── Scan API functions ───────────────────────────────────────────────────────

export async function getPackingScanStatus(id: number): Promise<PackingScanStatus> {
  const r = await api.get<PackingScanStatus>(`/packing/${id}/scan`);
  return r.data;
}

export async function addPackingSerial(
  id: number,
  packingItemId: number,
  serialNo: string,
): Promise<{ serial: PackingSerialEntry; newPackedQty: number }> {
  const r = await api.post(`/packing/${id}/scan/add`, { packingItemId, serialNo });
  return r.data;
}

/** Auto-match: scan any unit; the server routes it to the matching incomplete
 *  pick-list line. Returns which line it landed on. */
export async function addPackingSerialAuto(
  id: number,
  serialNo: string,
): Promise<{ serial: PackingSerialEntry; newPackedQty: number; matchedItemId: number; matchedItemName: string }> {
  const r = await api.post(`/packing/${id}/scan/add-auto`, { serialNo });
  return r.data;
}

export async function removePackingSerial(
  id: number,
  serialId: number,
): Promise<{ deleted: boolean; newPackedQty: number }> {
  const r = await api.delete(`/packing/${id}/scan/serial/${serialId}`);
  return r.data;
}

// ── Packing CRUD ─────────────────────────────────────────────────────────────

export async function listPackings(filter: { status?: string; channel?: string; from?: string; to?: string } = {}): Promise<Packing[]> {
  const r = await api.get<Packing[]>('/packing', { params: filter });
  return r.data;
}

/** Excel export of every packing matching the filters + search (no cap). */
export function packingExportPath(filter: { status?: string; channel?: string; from?: string; to?: string; search?: string }): string {
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(filter).filter(([, v]) => v != null && v !== '')) as Record<string, string>,
  ).toString();
  return `/packing/export.xlsx${qs ? `?${qs}` : ''}`;
}

export async function getPacking(id: number): Promise<Packing> {
  const r = await api.get<Packing>(`/packing/${id}`);
  return r.data;
}

export async function updatePacking(
  id: number,
  payload: {
    supervisor?: string;
    sealNo?: string;
    remarks?: string;
    items?: { packingItemId: number; packedQty: number; condition?: PackingItem['condition']; conditionNote?: string }[];
    boxes?: { boxNo: number; itemCode: string; qty: number; weightKg?: number; lengthCm?: number; widthCm?: number; heightCm?: number }[];
  },
): Promise<Packing> {
  const r = await api.patch<Packing>(`/packing/${id}`, payload);
  return r.data;
}

export async function supervisorConfirmPacking(id: number, supervisor: string): Promise<Packing> {
  const r = await api.post<Packing>(`/packing/${id}/supervisor-confirm`, { supervisor });
  return r.data;
}

/** Online (Shopify) orders: the dispatch note raised automatically on verification. */
export type AutoDispatch = { grnNo: string; status: string } | { error: string } | null;

export async function managerConfirmPacking(id: number): Promise<Packing & { autoDispatch?: AutoDispatch }> {
  const r = await api.post<Packing & { autoDispatch?: AutoDispatch }>(`/packing/${id}/manager-confirm`);
  return r.data;
}

export async function rejectPacking(id: number, reason: string): Promise<Packing> {
  const r = await api.post<Packing>(`/packing/${id}/reject`, { reason });
  return r.data;
}

// ── Assorted box-builder ─────────────────────────────────────────────────────

export interface BoxLine {
  id: number;
  itemId: number | null;
  itemCode: string | null;
  itemName: string;
  qty: number;
  sourceSerialNo: string | null;
}

export interface BuilderBox {
  id: number;
  boxNo: number;
  tareWeightKg: number | null;
  grossWeightKg: number | null;
  netWeightKg: number | null;
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
  items: BoxLine[];
}

export async function listBoxes(packingId: number): Promise<BuilderBox[]> {
  const r = await api.get<BuilderBox[]>(`/packing/${packingId}/boxes`);
  return r.data;
}

export interface SerialPreview {
  found: boolean;
  itemCode?: string;
  itemName?: string;
  available?: number;
  status?: string;
  dispatched?: boolean;
  onPickList?: boolean;
  /** Box numbers of this packing that already hold this barcode. */
  inThisPackingBoxes?: number[];
}

export async function previewSerial(packingId: number, code: string): Promise<SerialPreview> {
  const r = await api.get<SerialPreview>(`/packing/${packingId}/serial-preview`, {
    params: { code },
  });
  return r.data;
}

export async function createBox(packingId: number, tareWeightKg?: number): Promise<{ id: number }> {
  const r = await api.post(`/packing/${packingId}/boxes`, { tareWeightKg });
  return r.data;
}

export async function addBoxItem(
  boxId: number,
  serialNo: string,
  qty: number,
): Promise<{ itemCode: string; packedQty: number; serialRemaining: number }> {
  const r = await api.post(`/packing/boxes/${boxId}/items`, { serialNo, qty });
  return r.data;
}

export async function setBoxWeight(
  boxId: number,
  grossWeightKg: number,
  tareWeightKg?: number,
  dims?: { lengthCm?: number; widthCm?: number; heightCm?: number },
): Promise<BuilderBox> {
  const r = await api.patch(`/packing/boxes/${boxId}/weight`, { grossWeightKg, tareWeightKg, ...dims });
  return r.data;
}

export async function removeBoxItem(boxItemId: number): Promise<{ deleted: boolean }> {
  const r = await api.delete(`/packing/box-items/${boxItemId}`);
  return r.data;
}

export async function removeBox(boxId: number): Promise<{ deleted: boolean }> {
  const r = await api.delete(`/packing/boxes/${boxId}`);
  return r.data;
}


/** Carton details last saved for an item (most recent box that held it) — prefills a new box. */
export interface ItemBoxDefaults {
  boxId: number;
  packNo: string;
  singleItem: boolean;
  qty: number;
  tareWeightKg: number | null;
  grossWeightKg: number | null;
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
}
export async function getItemBoxDefaults(itemCode: string, excludeBoxId?: number): Promise<ItemBoxDefaults | null> {
  const r = await api.get<ItemBoxDefaults | ''>('/packing/boxes/item-defaults', { params: { itemCode, excludeBoxId } });
  return r.data || null;
}
